import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Phone, MapPin, Clock, BadgeCheck, Star, Stethoscope, Award, GraduationCap, Briefcase, ShieldCheck } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { COUNTRIES } from "../lib/countries";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function HealthSearch() {
  const [cats, setCats] = useState([]);
  const [prefs, setPrefs] = useState([]);
  const [f, setF] = useState({ profession: "", country: "", shift: "", max_wage: "", employment_pref: "", min_years: "", q: "", days: [], verified_only: false });
  const [workers, setWorkers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const search = async () => {
    setLoading(true);
    try {
      const params = {};
      if (f.profession) params.profession = f.profession;
      if (f.country) params.country = f.country;
      if (f.shift) params.shift = f.shift;
      if (f.max_wage) params.max_wage = f.max_wage;
      if (f.employment_pref) params.employment_pref = f.employment_pref;
      if (f.min_years) params.min_years = f.min_years;
      if (f.q) params.q = f.q;
      if (f.days.length) params.days = f.days.join(",");
      if (f.verified_only) params.verified_only = true;
      const r = await api.get("/employer/health/search", { params });
      setWorkers(r.data.workers);
      setTotal(r.data.total);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/flex/categories").then((r) => { setCats(r.data.health || []); setPrefs(r.data.employment_prefs || []); }).catch(() => {});
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDay = (d) => setF((s) => ({ ...s, days: s.days.includes(d) ? s.days.filter((x) => x !== d) : [...s.days, d] }));

  return (
    <div className="space-y-6 fade-up" data-testid="health-search">
      <div className="border border-[#0A0A0A] p-5 space-y-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <select data-testid="health-filter-profession" value={f.profession} onChange={(e) => setF({ ...f, profession: e.target.value })} className="border border-[#0A0A0A] px-3 py-2 text-sm">
            <option value="">All professions</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="health-filter-country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="border border-[#0A0A0A] px-3 py-2 text-sm">
            <option value="">All countries</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="health-filter-employment" value={f.employment_pref} onChange={(e) => setF({ ...f, employment_pref: e.target.value })} className="border border-[#0A0A0A] px-3 py-2 text-sm">
            <option value="">Any employment</option>
            {prefs.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select data-testid="health-filter-shift" value={f.shift} onChange={(e) => setF({ ...f, shift: e.target.value })} className="border border-[#0A0A0A] px-3 py-2 text-sm">
            <option value="">Any shift</option>
            {["Day", "Evening", "Night", "Flexible"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input data-testid="health-filter-wage" type="number" placeholder="Max $/hr" value={f.max_wage} onChange={(e) => setF({ ...f, max_wage: e.target.value })} className="border border-[#0A0A0A] px-3 py-2 text-sm" />
          <input data-testid="health-filter-years" type="number" placeholder="Min clinical yrs" value={f.min_years} onChange={(e) => setF({ ...f, min_years: e.target.value })} className="border border-[#0A0A0A] px-3 py-2 text-sm" />
          <input data-testid="health-filter-q" placeholder="Specialty / city / license" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && search()} className="border border-[#0A0A0A] px-3 py-2 text-sm lg:col-span-2" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono2 text-xs uppercase text-[#4B5563]">Days:</span>
          {DAYS.map((d) => (
            <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`health-filter-day-${d}`}
              className={`px-2.5 py-1 text-xs font-medium border ${f.days.includes(d) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#0A0A0A]"}`}>{d}</button>
          ))}
          <button
            type="button"
            data-testid="health-filter-verified"
            onClick={() => setF((s) => ({ ...s, verified_only: !s.verified_only }))}
            className={`px-3 py-1 text-xs font-medium border inline-flex items-center gap-1.5 transition-colors ${f.verified_only ? "bg-[#0E7C66] text-white border-[#0E7C66]" : "border-[#0E7C66] text-[#0E7C66] hover:bg-[#0E7C66]/10"}`}
          >
            <ShieldCheck size={13} /> Verified only
          </button>
          <button data-testid="health-search-btn" onClick={search} className="btn-ink px-4 py-1.5 text-xs font-medium ml-auto inline-flex items-center gap-1.5">
            <Send size={13} /> Search
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-[#4B5563]"><Loader2 className="animate-spin inline mr-2" size={16} /> Searching…</div>
      ) : workers.length === 0 ? (
        <div className="border-2 border-dashed border-[#E5E7EB] p-10 text-center text-sm text-[#4B5563]" data-testid="health-empty">No healthcare workers match yet. Try widening your filters.</div>
      ) : (
        <>
          <p className="font-mono2 text-xs text-[#4B5563]">{total} healthcare worker{total === 1 ? "" : "s"} found</p>
          <div className="grid md:grid-cols-2 gap-4" data-testid="health-results">
            {workers.map((w) => <HealthCard key={w.id} w={w} />)}
          </div>
        </>
      )}
    </div>
  );
}

function Stars({ value, size = 13 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} className={n <= Math.round(value) ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#D1D5DB]"} />
      ))}
    </span>
  );
}

function HealthCard({ w }) {
  const loc = [w.location, w.country].filter(Boolean).join(", ");
  const [rating, setRating] = useState(w.rating_avg || 0);
  const [count, setCount] = useState(w.rating_count || 0);
  const [open, setOpen] = useState(false);
  const [myStars, setMyStars] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!myStars) { toast.error("Pick a star rating first."); return; }
    setSaving(true);
    try {
      await api.post(`/employer/flex/${w.id}/review`, { rating: myStars, comment });
      const nc = count + 1;
      setRating(Math.round(((rating * count) + myStars) / nc * 10) / 10);
      setCount(nc);
      setOpen(false);
      toast.success("Review submitted.");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[#0A0A0A] p-5 space-y-3" data-testid={`health-card-${w.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-bold text-lg tracking-tight flex items-center gap-1.5">
            {w.name || "Healthcare worker"}
            {w.credential_verified && <ShieldCheck size={16} className="text-[#0E7C66]" title="Credential Verified" />}
            {w.verified && !w.credential_verified && <BadgeCheck size={16} className="text-[#10B981]" title="Verified worker" />}
          </div>
          <div className="text-xs text-[#4B5563] flex items-center gap-1 mt-0.5"><MapPin size={12} /> {loc || "—"}</div>
          {w.credential_verified && (
            <span className="font-mono2 text-[10px] uppercase text-[#0E7C66] border border-[#0E7C66]/40 bg-[#0E7C66]/10 px-1.5 py-0.5 inline-flex items-center gap-1 mt-1.5" data-testid="health-card-credential">
              <ShieldCheck size={10} /> Credential Verified
            </span>
          )}
          {count > 0 && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <Stars value={rating} />
              <span className="font-mono2 text-xs text-[#4B5563]">{rating} ({count})</span>
            </div>
          )}
        </div>
        <div className="font-display font-black text-2xl tracking-tighter text-[#0E7C66]" data-testid="health-card-score">{w.match_score}%</div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {(w.categories || []).map((c) => <span key={c} className="font-mono2 text-[10px] uppercase bg-[#0E7C66]/10 text-[#0E7C66] border border-[#0E7C66]/30 px-2 py-0.5 inline-flex items-center gap-1"><Stethoscope size={10} /> {c}</span>)}
        {w.specialty && <span className="font-mono2 text-[10px] uppercase bg-[#F3F4F6] border border-[#E5E7EB] px-2 py-0.5">{w.specialty}</span>}
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        {w.license_type && <span className="text-[#4B5563] inline-flex items-center gap-1"><Award size={13} /> {w.license_type}</span>}
        {w.clinical_years != null && <span className="text-[#4B5563] inline-flex items-center gap-1"><GraduationCap size={13} /> {w.clinical_years} yrs clinical</span>}
        {w.employment_pref && <span className="text-[#4B5563] inline-flex items-center gap-1"><Briefcase size={13} /> {w.employment_pref}</span>}
        {w.desired_hourly_wage != null && <span className="font-medium">${w.desired_hourly_wage}/hr</span>}
        {w.shift && <span className="text-[#4B5563] inline-flex items-center gap-1"><Clock size={13} /> {w.shift}</span>}
      </div>
      {(w.days || []).length > 0 && <div className="font-mono2 text-xs text-[#4B5563]">{w.days.join(" · ")}</div>}
      {w.skills?.length > 0 && <div className="text-xs text-[#4B5563]">Skills: {w.skills.join(", ")}</div>}
      {w.summary && <p className="text-sm text-[#4B5563] line-clamp-2">{w.summary}</p>}

      {open && (
        <div className="border border-[#E5E7EB] p-3 space-y-2" data-testid="health-review-form">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" data-testid={`health-review-star-${n}`} onClick={() => setMyStars(n)}>
                <Star size={22} className={n <= myStars ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#D1D5DB] hover:text-[#F59E0B]"} />
              </button>
            ))}
          </div>
          <textarea data-testid="health-review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Optional comment…" className="w-full border border-[#0A0A0A] px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#0E7C66]" />
          <div className="flex gap-2">
            <button data-testid="health-review-submit" onClick={submit} disabled={saving} className="btn-ink px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Star size={13} />} Submit
            </button>
            <button onClick={() => setOpen(false)} className="btn-outline px-3 py-1.5 text-xs font-medium">Cancel</button>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <a href={`tel:${w.phone}`} data-testid="health-card-call" className="btn-ink flex-1 py-2 text-sm font-medium inline-flex items-center justify-center gap-2">
          <Phone size={15} /> {w.phone || "No phone"}
        </a>
        <button data-testid="health-rate-btn" onClick={() => setOpen((o) => !o)} className="btn-outline px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5">
          <Star size={15} /> Rate
        </button>
      </div>
    </div>
  );
}
